import { FC } from 'react';

import { DownloadIcon, XIcon } from '@heroicons/react/outline';
import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { GiftSupplySettlement } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';

import { formatDate } from '../../giftSeller/utils/delivery.utils';
import { formatWon, ceilWon } from '../../giftSeller/utils/settlement.utils';
import { splitLineSurcharge, statusColor, statusLabel } from '../utils/settlement.utils';
import { exportSupplySettlementDetailExcel } from '../utils/supplySettlementExcel';

// ─────────────────────────────────────────────
// SupplySettlementDetailModal
//
// 정산 완료 내역의 "상세보기" 를 넓은 모달로 보여준다.
// 공급사(HistoryYearCalendar) / 관리자(AdminHistoryTable) 공용.
// ─────────────────────────────────────────────

/** `SupplySettlementDetailModal` 컴포넌트의 props */
export interface SupplySettlementDetailModalProps {
  /** 상세를 표시할 정산 문서 (null이면 모달 닫힘) */
  settlement: GiftSupplySettlement | null;
  /**
   * 공급사 본인의 정산정보.
   * `splitLineSurcharge`에서 소량 추가단가/택배비 분해 시 사용한다.
   * 관리자 뷰에서는 `undefined`를 전달한다.
   */
  mySettlementInfo?: SettlementInfo;
  /** 모달 닫기 핸들러 */
  onClose: () => void;
}

export const SupplySettlementDetailModal: FC<SupplySettlementDetailModalProps> = ({
  settlement,
  mySettlementInfo,
  onClose,
}) => {
  if (!settlement) return null;

  const s = settlement;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-gray-900">
              {s.year}년 {s.month}월 정산 상세
            </h2>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(s.status)}`}
            >
              {statusLabel(s.status)}
            </span>
            {s.executedAt && (
              <span className="text-xs text-gray-500">
                지급일 {formatDate(s.executedAt)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportSupplySettlementDetailExcel(s, mySettlementInfo)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <DownloadIcon className="h-4 w-4" />
              엑셀 다운로드
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 요약 */}
        <div className="flex items-center gap-6 border-b border-gray-100 px-5 py-3 text-sm">
          <div>
            <span className="text-gray-500">건수 </span>
            <span className="font-semibold text-gray-800">{s.lines.length}건</span>
          </div>
          <div>
            <span className="text-gray-500">정산금액(VAT 포함) </span>
            <span className="font-semibold text-rose-700">
              {formatWon(ceilWon(s.totalAmount))}
            </span>
          </div>
        </div>

        {/* 라인 상세 (스크롤) — 정산 예정 테이블과 동일한 컬럼 구조.
            배송 스냅샷(배송요청일/배송완료일/택배사/송장번호)은 정산 라인에 없어 '-'로 표시 */}
        <div className="overflow-y-auto px-5 py-4">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">상품</th>
                <th className="px-3 py-2 text-left">배송요청일</th>
                <th className="px-3 py-2 text-left">배송완료일</th>
                <th className="px-3 py-2 text-left">택배사</th>
                <th className="px-3 py-2 text-left">송장번호</th>
                <th className="px-3 py-2 text-right">도매단가(+소량)</th>
                <th className="px-3 py-2 text-right">× 수량</th>
                <th className="px-3 py-2 text-right">+ 택배비</th>
                <th className="px-3 py-2 text-right">= 정산금액(VAT 포함)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {s.lines.map((line, idx) => {
                const { extraUnitAmount, courierFee } = splitLineSurcharge(
                  line.quantity,
                  line.surcharge,
                  mySettlementInfo,
                );
                const extraUnitPrice =
                  line.quantity > 0 ? extraUnitAmount / line.quantity : 0;
                // populate된 배송 (string이면 미populate → 배송 정보 '-')
                const delivery =
                  typeof line.giftDelivery === 'object' ? line.giftDelivery : null;
                return (
                  <tr key={delivery?._id ?? idx} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <p className="font-medium" title={line.item.name || '-'}>
                        {line.item.name || '-'}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                      {delivery?.recipient?.submittedAt
                        ? formatDate(delivery.recipient.submittedAt)
                        : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                      {delivery?.shipping?.deliveredAt
                        ? formatDate(delivery.shipping.deliveredAt)
                        : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-600">
                      {delivery?.shipping?.carrier || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-600">
                      {delivery?.shipping?.trackingNumber || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      {line.unitAmount.toLocaleString()}
                      {extraUnitPrice > 0 && (
                        <span className="text-amber-600">
                          (+{extraUnitPrice.toLocaleString()})
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      {line.quantity}개
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      {courierFee > 0 ? (
                        courierFee.toLocaleString()
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-gray-800">
                      {formatWon(line.lineAmount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t border-gray-200 text-sm font-semibold">
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={6}>
                  합계 ({s.lines.length}건)
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                  {s.lines.reduce((sum, l) => sum + l.quantity, 0)}개
                </td>
                <td />
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-rose-700">
                  {formatWon(ceilWon(s.totalAmount))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};
